import { ChartType } from "~/constants/graphs"
import SuperChart, { type PlotLine, type CategoryChartSeries } from "./SuperChart"

/**
 * @param threshold - A decimal value between 0 and 1, if the relative value of data compared to the dataset exceeds this threshold, the bar changes to the defined color
 * @param color - String value defining a color
 */
export type ColorThreshold = { threshold: number; color: string }

/**
 * @param name - The name of the datapoint's label/category
 * @param value - Numeric value defining the position of the datapoint on the Y-axis (height)
 */
export type StaticBarChartDataPoint = { name: string; value: number; color?: string }

/**
 * @param data - Array of datapoints to display on the chart
 * @param color - Default color of a bar if its relative y-value is lower than lowest defined threshold, or if no thresholds are defined
 * @param thresholds - Array of thresholds defining bar colors relative to the highest value in the dataset
 */
export interface StaticBarChartProps {
  data: Array<StaticBarChartDataPoint>
  color: string
  thresholds?: Array<ColorThreshold>
  plotLines?: { x?: PlotLine[]; y?: PlotLine[] }
  [propName: string]: any
}

const StaticBarChart = ({ data, color, thresholds, plotLines, ...props }: StaticBarChartProps) => {
  const minValue = Math.min(...data.map((d) => d.value))
  const maxValue = Math.max(...data.map((d) => d.value)) - minValue || 1
  // Sort the thresholds in descending order so that we can iterate through them, ending with the default color
  const sortedThresholds =
    thresholds == null ? [] : [...thresholds].sort((a, b) => b.threshold - a.threshold)

  const getBarColor = (relativeValue: number): string => {
    for (const t of sortedThresholds) {
      if (relativeValue > t.threshold) {
        return t.color
      }
    }
    return color
  }

  const chartData: CategoryChartSeries = {
    colors: [color],
    name: "",
    type: "column",
    data: data.map<{ category: string; value: number; color?: string }>((val) => {
      const { name, value } = val
      const relativeValue = (value - minValue) / maxValue
      const color = getBarColor(relativeValue)
      return {
        category: name,
        value: relativeValue * 0.85 + 0.15, // Adjust bar heights so even the lowest value at least appears on the chart
        color,
      }
    }),
  }

  return (
    <SuperChart
      type={ChartType.CategorySeries}
      data={[chartData]}
      showTooltip={false}
      compact={true}
      showYAxisLabels={false}
      showXAxisLabels={true}
      showTickLines={false}
      xAxisYOffset={16}
      disableAnimation={true}
      plotLines={plotLines}
      {...props}
    />
  )
}

export default StaticBarChart
